// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
import React, { useEffect, useState } from "react";
import FeatureComparison from "../../subscription/components/FeatureComparison";
import { textColors, typography } from "../../design-system/tokens";
import { fetchSubscriptionPlans } from "../utils/affiliateApi";

/** Plans worth comparing here — the two a coupon can grant. */
const COMPARED_PLAN_NAMES = ["plus", "pro"];

/**
 * "What is the difference between the Plus and Pro plans?" — collapsed by default,
 * expanding into the same feature table the account settings page shows.
 *
 * Plans are fetched lazily on first open: most buyers already know the difference,
 * so the request should not be paid for on every page load.
 */
const PlanDifferenceDisclosure = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [plans, setPlans] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // `isLoading` is deliberately NOT a dependency: setting it would re-run this
  // effect, and the cleanup would cancel the very fetch it just started, leaving
  // the panel loading forever. `plans` guards against re-fetching instead.
  useEffect(() => {
    if (!isOpen || plans) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setError("");
    (async () => {
      try {
        const data = await fetchSubscriptionPlans();
        if (cancelled) return;
        const all = Array.isArray(data?.plans) ? data.plans : [];
        // Keep the canonical Plus-then-Pro ordering regardless of API order.
        const compared = COMPARED_PLAN_NAMES.map((name) =>
          all.find((plan) => String(plan?.name).toLowerCase() === name)
        ).filter(Boolean);
        setPlans(compared);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Could not load the plan comparison.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, plans]);

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-controls="plan-difference-panel"
        className={[
          "inline-flex items-center gap-1.5 rounded-md",
          typography.bodySmall,
          textColors.secondary,
          "underline underline-offset-2 hover:brightness-110",
          "focus:outline-none focus:ring-2 focus:ring-pink-400 dark:focus:ring-pink-300",
        ].join(" ")}
      >
        What is the difference between the Plus and Pro plans?
        <svg
          className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen ? (
        <div id="plan-difference-panel">
          {isLoading ? (
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>Loading plan comparison…</p>
          ) : error ? (
            <p className={`${typography.bodySmall} text-red-600 dark:text-red-400`} role="alert">
              {error}
            </p>
          ) : plans && plans.length > 0 ? (
            <FeatureComparison plans={plans} />
          ) : (
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>
              Plan details are unavailable right now.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default PlanDifferenceDisclosure;
